<script setup>
// SiteUp (#53): indicador AO VIVO de que a IA está compondo a resposta.
// Escuta o evento 'captainProcessing' (broadcast do AgentRunnerService via
// ActionCable) e mostra: ícone girando + tempo decorrido + tools que a IA está
// usando em tempo real. Some sozinho no 'done' (ou por segurança em 2min).
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { labelForTool } from 'shared/helpers/captainToolLabels';
import ThinkingOrb from 'next/orb/ThinkingOrb.vue';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
});

const processing = ref(false);
const tools = ref([]);
const startedAt = ref(0);
const elapsed = ref(0);
let ticker = null;
let safety = null;

// Rótulos PT-BR das tools: fonte única em shared/helpers/captainToolLabels
// (mesmo mapa usado no rodapé persistente sob a bolha em Message.vue).
const prettyTools = computed(() => [...new Set(tools.value)].map(labelForTool));

const elapsedLabel = computed(() => {
  const s = elapsed.value;
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
});

// Estado em linguagem natural (estilo "Thinking…/Using tool" do Claude): sem tool
// ainda → "pensando"; assim que chama alguma ferramenta → "usando ferramentas"
// (as ferramentas em si aparecem ao lado, com um ícone de ferramenta).
const stateLabel = computed(() =>
  prettyTools.value.length ? 'IA usando ferramentas' : 'IA pensando'
);

// SITEUP V2 (F1.3): estado do Thinking Orb acompanha a fase real —
// working = gerando resposta · solving = executando ferramenta.
const orbState = computed(() =>
  prettyTools.value.length ? 'solving' : 'working'
);

const stop = () => {
  processing.value = false;
  if (ticker) clearInterval(ticker);
  if (safety) clearTimeout(safety);
  ticker = null;
  safety = null;
};

const start = () => {
  startedAt.value = Date.now();
  elapsed.value = 0;
  tools.value = [];
  processing.value = true;
  if (ticker) clearInterval(ticker);
  ticker = setInterval(() => {
    elapsed.value = Math.floor((Date.now() - startedAt.value) / 1000);
  }, 1000);
  if (safety) clearTimeout(safety);
  safety = setTimeout(stop, 120000);
};

// SiteUp (#82): tempo mínimo de exibição — quando o 'done' chega logo após o
// 'started' (geração rápida), o indicador sumia rápido demais pra ser percebido.
// Garante pelo menos MIN_VISIBLE_MS de exibição. O cronômetro conta desde o 1º
// 'started' (não reinicia a cada evento), abrangendo o debounce + a geração.
const MIN_VISIBLE_MS = 4000;

const onProcessing = data => {
  if (!data || String(data.conversation_id) !== String(props.conversationId)) {
    return;
  }
  if (data.status === 'done') {
    const visibleFor = Date.now() - startedAt.value;
    if (processing.value && visibleFor < MIN_VISIBLE_MS) {
      if (safety) clearTimeout(safety);
      safety = setTimeout(stop, MIN_VISIBLE_MS - visibleFor);
    } else {
      stop();
    }
    return;
  }
  if (!processing.value) start();
  if (data.tools && data.tools.length) tools.value = data.tools;
};

watch(() => props.conversationId, stop);
onMounted(() => emitter.on('captainProcessing', onProcessing));
onBeforeUnmount(() => {
  emitter.off('captainProcessing', onProcessing);
  stop();
});
</script>

<template>
  <div v-if="processing" class="flex w-full mb-3 justify-end px-4">
    <div
      role="status"
      aria-live="polite"
      class="inline-flex flex-col items-end gap-2 max-w-[85%] ltr:ml-8 rtl:mr-8 rounded-2xl px-3 py-3 text-xs bg-n-slate-3 dark:bg-n-solid-2 text-n-slate-12"
    >
      <ThinkingOrb
        :state="orbState"
        :size="64"
        :label="stateLabel"
        class="shrink-0"
      />
      <div class="inline-flex items-center gap-2 min-w-0">
        <span class="font-medium">{{ stateLabel }}…</span>
        <span class="text-n-slate-11 tabular-nums">{{ elapsedLabel }}</span>
        <span
          v-if="prettyTools.length"
          class="inline-flex items-center gap-1 min-w-0 text-n-slate-10"
        >
          <span class="shrink-0" aria-hidden="true">·</span>
          <span class="i-ri-wrench-line size-3 shrink-0" aria-hidden="true" />
          <span class="truncate">{{ prettyTools.join(' · ') }}</span>
        </span>
      </div>
    </div>
  </div>
</template>
